import React, { useState, useEffect } from 'react';
import TagSelector from '../tag-selector';
import { authorizedFetch } from '../../utils';
import {
  createContextEventUrl,
  NESTED_FIELDS,
  getFlattenedEventMapping,
  recencyFilters
} from '../../reporting-utils';

const fieldAbortControllers = {};

const getUniqueEventTerms = async (
  flowIds,
  field,
  setUniqueEventTerms,
  addError,
  filterText = '',
  exclude = []
) => {
  if (fieldAbortControllers[field]) {
    fieldAbortControllers[field].abort();
  }
  fieldAbortControllers[field] = new AbortController();

  const isFiltered =
    !Array.isArray(flowIds) || flowIds.length === 0 || !!filterText;

  const nestedField = NESTED_FIELDS.find(f => f.field === field);

  let fieldToQueryWith = field;
  const mapping = await getFlattenedEventMapping();
  const isTextField = mapping[field] === 'text';
  if (isTextField) {
    fieldToQueryWith += '.keyword';
  }

  //let terms = { field: fieldToQueryWith, exclude: [...exclude] };

  let aggs = {
    fields: {
      terms: { field: fieldToQueryWith, exclude: [...exclude] }
    }
  };

  if (isFiltered) {
    aggs = {
      matchingSearch: {
        filter: {
          bool: {
            must: []
          }
        },
        aggs: aggs
      }
    };

    if (Array.isArray(flowIds) && flowIds.length > 0) {
      aggs.matchingSearch.filter.bool.must.push({
        terms: {
          'properties.flowId.keyword': flowIds
        }
      });
    }
    if (filterText) {
      if (isTextField) {
        //use full text matching to find matches
        aggs.matchingSearch.filter.bool.must.push({
          match_phrase_prefix: { [field]: filterText }
        });
      } else {
        aggs.matchingSearch.filter.bool.must.push({
          prefix: { [field]: filterText }
        });
      }
    }
  }

  if (nestedField) {
    aggs = {
      nestedFields: {
        nested: {
          path: nestedField.path
        },
        aggs
      }
    };
  }

  const query = {
    aggs,
    size: 0
  };

  authorizedFetch(
    createContextEventUrl({
      recency: recencyFilters.LAST_30_DAYS
    }),
    'POST',
    query,
    fieldAbortControllers[field].signal
  )
    .then(response => {
      let responseBucket = response.aggregations;

      //nesting is at root, so move to that bucket, if necessary
      if (nestedField) {
        responseBucket = responseBucket.nestedFields;
      }

      //if query is filtered, move to that bucket
      if (isFiltered) {
        responseBucket = responseBucket.matchingSearch.fields;
      } else {
        //just grab the current fields from our current bucket
        responseBucket = responseBucket.fields;
      }

      setUniqueEventTerms(
        responseBucket.buckets.map(b => b.key_as_string || b.key)
      );
    })
    .catch(ex => {
      if (ex.name !== 'AbortError') {
        addError('Unable to get event terms', JSON.stringify(ex));
      }
    });
};

const EventTagSelector = ({
  flowIds,
  field,
  tags,
  onTagAdd,
  onTagRemove,
  addError,
  disabled,
  size = 'medium',
  onBulkAdd,
  onClear
}) => {
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tagSuggestionSearch, setTagSuggestionSearch] = useState('');

  useEffect(() => {
    if (!disabled) {
      getUniqueEventTerms(
        flowIds,
        field,
        setTagSuggestions,
        addError,
        tagSuggestionSearch,
        tags
      );
    }
  }, [addError, tagSuggestionSearch, tags, field, disabled, flowIds]);

  return (
    <TagSelector
      size={size}
      disabled={disabled}
      tags={tags}
      onTagAdd={onTagAdd}
      onTagRemove={onTagRemove}
      suggestions={tagSuggestions}
      onTextChange={setTagSuggestionSearch}
      placeholder='Add Value'
      width='auto'
      onBulkAdd={onBulkAdd}
      onClear={onClear}
    />
  );
};
EventTagSelector.displayName = 'EventTagSelector';

export default EventTagSelector;
